import { Box, Button, Icon } from '@mui/material';
import moment from 'moment-timezone';
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../../AppContext';
import { Actions } from '../../enums/ActionEnums';
import { Types } from '../../enums/TypeEnums';
import {
  GetEtfsDocument,
  IEtf,
  IGetEtfsQuery,
  useDeleteEtfMutation,
  useGetEtfsQuery
} from '../../generated/graphql';
import { DATE_TIME_FORMATS } from '../../utils/constants';
import { canUserCreate } from '../../utils/rightUtils';
import { iconStyle } from '../../utils/styleUtils';
import { Colors } from '../../utils/theme';
import OverviewTable from '../Common/Tables/OverviewTable';
import EtfForm from './EtfForm';

interface IProps {
  indexOfActiveTab: number;
}

const EtfList: React.FunctionComponent<IProps> = ({ indexOfActiveTab }: IProps) => {
  const navigate = useNavigate();
  const { dispatch, state } = useContext(AppContext);
  const { data, loading } = useGetEtfsQuery();
  const [allData, setAllData] = useState<IEtf[]>([]);
  const [openForm, setOpenForm] = useState(false);
  const [etf, setEtf] = useState<IEtf | null>(null);
  const [deleteEtf] = useDeleteEtfMutation();
  const canCreate = canUserCreate(state.user, Types.ETF);

  useEffect(() => {
    if (data && data.result) {
      setAllData(data.result as IEtf[]);
    }
  }, [data]);

  const formatDate = (rowData: IEtf, name: string) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return rowData[name]
      ? // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        moment(parseInt(rowData[name] as string)).format(DATE_TIME_FORMATS.DATE)
      : '';
  };

  const renderHistoryColumn = (rowData: IEtf) => (
    <Button id={rowData.id} onClick={() => handleHistory(rowData)}>
      <Icon sx={{ ...iconStyle, color: Colors.skyBlue }}>history</Icon>
    </Button>
  );

  const handleHistory = (rowData: IEtf) => {
    navigate(`/etfs/${rowData.id}`, {
      state: { ...rowData, indexOfActiveTab }
    });
  };

  const handleRemove = (rowData: IEtf) => {
    dispatch({
      type: Actions.ShowConfirmation,
      payload: {
        text: `Are you sure you want to delete ${rowData.name} etf??`,
        agreeAction: handleDelete,
        params: rowData
      }
    });
  };

  const handleDelete = (rowData: IEtf) => {
    dispatch({
      type: Actions.HideConfirmation
    });
    deleteEtf({
      variables: {
        id: rowData.id
      },
      update(cache) {
        const etfsQuery = cache.readQuery({
          query: GetEtfsDocument
        }) as IGetEtfsQuery;
        if (etfsQuery.result) {
          const cachedEtf = etfsQuery.result.findIndex((e) => e?.id === rowData.id);
          if (cachedEtf > -1) {
            const data = etfsQuery.result.filter((e) => e?.id !== rowData.id);
            cache.writeQuery({
              query: GetEtfsDocument,
              data: { result: data }
            });
          }
        }
      }
    }).catch((e) => {
      dispatch({
        type: Actions.ShowMessage,
        payload: { text: e.message, severity: 'error' }
      });
    });
    dispatch({
      type: Actions.ShowMessage,
      payload: {
        text: `${rowData.name} etf deleted`,
        severity: 'success'
      }
    });
  };

  const columns = [
    {
      title: 'Details',
      width: 60,
      sorting: false
    },
    {
      title: 'History',
      width: 60,
      sorting: false,
      render: renderHistoryColumn
    },
    {
      title: 'Launch Date',
      field: 'launch_date',
      width: 120,
      type: 'date',
      render: (rowData: IEtf) => formatDate(rowData, 'launch_date')
    },
    {
      title: 'Provider',
      field: 'provider',
      width: 265
    },
    {
      title: 'Name',
      field: 'name',
      width: 655
    },
    {
      title: 'Benchmark name',
      field: 'identifier',
      width: 490
    },
    {
      title: 'Remove',
      width: 71,
      sorting: false
    }
  ];

  const handleDetails = (rowData: IEtf) => {
    setEtf(rowData);
    setOpenForm(true);
  };

  const handleClose = (saved: boolean, isNew?: boolean, etfInput?: IEtf) => {
    if (saved && isNew) {
      addEtfDocument(etfInput as IEtf);
    }
    setEtf(null);
    setOpenForm(false);
  };

  const addEtfDocument = (etfInput: IEtf) => {
    const existing = allData.find((d) => d.id === etfInput.id);
    if (!existing) {
      setAllData([etfInput, ...allData]);
    }
  };

  return (
    <Box className="overviewPage">
      <Box className="print\:w-full">
        <OverviewTable
          columns={columns as []}
          data={allData}
          loading={loading}
          handleDetails={handleDetails}
          handleRemove={handleRemove}
          pageSize={15}
          createButtonProps={{
            id: 'create-etf-button',
            onClick: () => setOpenForm(true),
            text: 'Create ETF',
            disabled: !canCreate
          }}
        />
        {openForm && <EtfForm handleClose={handleClose} etfInput={etf} />}
      </Box>
    </Box>
  );
};

export default EtfList;
